import {
  TRACKER_GOING_STATES,
  type TrackerBookingSource,
  type TrackerGoingState,
  type TrackerGuest,
  type TrackerPatch,
} from '../services/tracker-api-client';

/**
 * Applies a patch to the local row so the cell settles immediately. The server
 * response replaces it moments later; this only covers the gap.
 */
export function applyOptimisticPatch(guest: TrackerGuest, patch: TrackerPatch): TrackerGuest {
  const next: TrackerGuest = { ...guest };
  if ('going' in patch) {
    next.going = patch.going ?? 'invited';
    next.goingOverride = patch.going ?? null;
  }
  if ('arrivalDate' in patch) next.arrivalDate = patch.arrivalDate ?? null;
  if ('departureDate' in patch) next.departureDate = patch.departureDate ?? null;
  if ('stayingAt' in patch) next.stayingAt = patch.stayingAt ?? null;
  if ('bookingSource' in patch) next.bookingSource = patch.bookingSource ?? null;
  if ('partySize' in patch) next.partySize = patch.partySize ?? null;
  if ('roomDetails' in patch) next.roomDetails = patch.roomDetails ?? null;
  if ('preferencesNotes' in patch) next.preferencesNotes = patch.preferencesNotes ?? null;
  // Contact edits write through to the invitee row, which is what `email`/
  // `phone` read from on the next load.
  if ('contactEmail' in patch) next.email = patch.contactEmail ?? null;
  if ('contactPhone' in patch) next.phone = patch.contactPhone ?? null;
  if (patch.customFields) {
    // Custom fields merge per section server-side, so the local copy has to
    // merge too — replacing the map would blank every other section until the
    // response lands, and the field the host just left would visibly revert.
    const merged: Record<string, Record<string, string>> = { ...(next.customFields ?? {}) };
    for (const [sectionKey, fields] of Object.entries(patch.customFields)) {
      if (fields === null) {
        delete merged[sectionKey];
        continue;
      }
      const section = { ...(merged[sectionKey] ?? {}) };
      for (const [label, value] of Object.entries(fields)) {
        if (value === null) delete section[label];
        else section[label] = value;
      }
      merged[sectionKey] = section;
    }
    next.customFields = merged;
  }
  return next;
}

/**
 * The patch that puts back exactly what `patch` changed, and nothing else.
 *
 * Restoring the whole captured row would revert a sibling field the host saved
 * on the same row while this one was in flight — which the editor produces as
 * ordinary typing, since it commits one field per request on blur. Built as a
 * patch rather than as a field-by-field copy so it goes back through
 * applyOptimisticPatch and there is only one place that knows a `contactEmail`
 * edit lands on `email`.
 */
/*
  The board types these two wide because they arrive as whatever the server
  stored, while a patch may only carry a known state. An unrecognized stored
  value undoes to null — clearing the override, which is the same safe default
  the table already renders it as, rather than echoing a value back that the
  server would reject and leaving the row stuck mid-edit.
*/
function knownGoingState(value: string | null | undefined): TrackerGoingState | null {
  return TRACKER_GOING_STATES.includes(value as TrackerGoingState)
    ? (value as TrackerGoingState)
    : null;
}

function knownBookingSource(value: string | null | undefined): TrackerBookingSource | null {
  return value === 'tripsby' || value === 'guest' ? value : null;
}

export function invertPatch(original: TrackerGuest, patch: TrackerPatch): TrackerPatch {
  const undo: TrackerPatch = {};
  if ('going' in patch) undo.going = knownGoingState(original.goingOverride);
  if ('arrivalDate' in patch) undo.arrivalDate = original.arrivalDate;
  if ('departureDate' in patch) undo.departureDate = original.departureDate;
  if ('stayingAt' in patch) undo.stayingAt = original.stayingAt;
  if ('bookingSource' in patch) undo.bookingSource = knownBookingSource(original.bookingSource);
  if ('partySize' in patch) undo.partySize = original.partySize;
  if ('roomDetails' in patch) undo.roomDetails = original.roomDetails;
  if ('preferencesNotes' in patch) undo.preferencesNotes = original.preferencesNotes;
  if ('contactEmail' in patch) undo.contactEmail = original.email;
  if ('contactPhone' in patch) undo.contactPhone = original.phone;
  if (patch.customFields) {
    const undoFields: Record<string, Record<string, string | null> | null> = {};
    for (const [sectionKey, fields] of Object.entries(patch.customFields)) {
      if (fields === null) {
        // The patch dropped the whole section, so put the whole section back.
        undoFields[sectionKey] = original.customFields?.[sectionKey] ?? null;
        continue;
      }
      // Only the labels this patch touched: null restores "was not there".
      const section: Record<string, string | null> = {};
      for (const label of Object.keys(fields)) {
        section[label] = original.customFields?.[sectionKey]?.[label] ?? null;
      }
      undoFields[sectionKey] = section;
    }
    undo.customFields = undoFields;
  }
  return undo;
}
